"use client";

import React from "react";
import { Building2, ShieldCheck, Landmark, FolderKanban, ArrowRight, Calculator, PhoneCall, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  id: "commercial",
  title: "Commercial scaffolding",
  description: "Fully compliant access structures, birdcages, and temporary roofs for commercial builds and refurbishments.",
  to: "/CommercialScaffoldingPage",
  tag: "Commercial",
  iconName: "building"
}, {
  id: "edge-protection",
  title: "Edge protection",
  description: "Engineered handrails, perimeter barriers, and mesh protection keeping roofing and site teams secure at height.",
  to: "/EdgeProtectionPage",
  tag: "Site Safety",
  iconName: "shield"
}, {
  id: "councils",
  title: "Councils & public sector",
  description: "Prequalified, audited scaffolding services for local authorities, housing associations, and municipal assets.",
  to: "/CouncilsPublicSectorPage",
  tag: "Public Sector",
  iconName: "landmark"
}, {
  id: "projects",
  title: "Projects & case studies",
  description: "Explore recent scaffolding handovers across Berkshire, Surrey, Hampshire, London, and the Home Counties.",
  to: "/ProjectsCaseStudiesPage",
  tag: "Portfolio",
  iconName: "projects"
}];
export default function RelatedServices() {
  const prefersReducedMotion = useReducedMotion();
  const getServiceIcon = (iconName: string) => {
    switch (iconName) {
      case "building":
        return <Building2 className="h-6 w-6 text-primary" />;
      case "shield":
        return <ShieldCheck className="h-6 w-6 text-primary" />;
      case "landmark":
        return <Landmark className="h-6 w-6 text-primary" />;
      case "projects":
        return <FolderKanban className="h-6 w-6 text-primary" />;
      default:
        return <Building2 className="h-6 w-6 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedservicesgrid" data-nav="light" className="bg-background py-20 md:py-28 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-3 text-foreground font-medium">
              Specialist Services
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
              Explore related access solutions
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-default">
              Find purpose-built scaffolding packages, site safety systems, and public sector support across the South East.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto shadow-sm">
              <Link to="/ContactRequestQuotePage">
                <Calculator className="h-4 w-4 mr-2" />
                Request a quote
              </Link>
            </Button>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => <motion.div key={service.id} variants={itemVariants} data-index={i}>
              <Card className="h-full flex flex-col justify-between border-border bg-card hover:border-primary/50 shadow-sm transition-[border-color,box-shadow] duration-200">
                <div>
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="p-2.5 rounded-lg bg-accent inline-flex items-center justify-center">
                        {getServiceIcon(service.iconName)}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {service.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground font-default">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground font-default">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 border-t border-border/50">
                  <Button asChild variant="ghost" className="w-full justify-between px-2 text-primary hover:text-primary hover:bg-accent font-medium group">
                    <Link to={service.to}>
                      <span>Learn more</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-14 p-6 sm:p-8 rounded-xl bg-secondary text-secondary-foreground shadow-md flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-semibold text-secondary-foreground font-default">
              Need a tailored site visit or fast turnaround quote?
            </h3>
            <div className="flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-secondary-foreground/80 pt-1">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                CHAS & NASC accredited
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                NASC compliant safety standards
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                20+ years trade experience
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                Request a quote
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground">
              <Link to="/AreasWeCoverPage">
                <PhoneCall className="h-4 w-4 mr-2" />
                Areas we cover
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}